import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth } from "../../firebase";
import { colors, spacing, typography } from "../../utils/designTokens";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../components/Toast";
import { GUIAS, buildGuiaHtml, guiaUrl } from "../../utils/guiasHabitia";
import { descargarFlyerPdf } from "../../utils/flyerExport";
import { loadContacto, topbarStyle, backBtn, ghostBtn, primaryBtn, descMenu, descItem } from "./materialesUi";
import { FaArrowLeft, FaLink, FaCheck, FaDownload, FaChevronDown, FaRegFilePdf, FaCode, FaExternalLinkAlt } from "react-icons/fa";

/**
 * Visor de las Guías Habitia de la web (solo lectura: el contenido canónico
 * vive en la landing). El comercial copia el link público para mandarlo por
 * WhatsApp/mail, o exporta PDF/HTML con el mismo diseño de la web — con los
 * CTA de WhatsApp apuntando a SU número (loadContacto), no al de Habitia.
 */
export default function GuiaViewer({ material, isMobile, onClose }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const email = userEmail || auth.currentUser?.email || "";
  const iframeRef = useRef(null);
  const [copiado, setCopiado] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [exportando, setExportando] = useState(false);

  const guia = GUIAS.find((g) => g.path === material.guiaPath) || GUIAS[0];
  const c0 = loadContacto(email);
  const agente = { name: c0.name || userName || "", email: c0.email || email, tel: c0.tel || "", cargo: "Comercial" };
  const html = useMemo(() => buildGuiaHtml(guia, agente), [guia]); // eslint-disable-line react-hooks/exhaustive-deps
  const url = guiaUrl(guia.path);

  useEffect(() => { setCopiado(false); }, [guia.path]);

  const fitIframe = () => {
    try {
      const h = iframeRef.current?.contentDocument?.body?.scrollHeight;
      if (h) iframeRef.current.style.height = `${h + 24}px`;
    } catch (_) {}
  };

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
      showToast("Link copiado — pegalo en WhatsApp o mail.", "success");
    } catch {
      showToast("No se pudo copiar el link.", "error");
    }
  }

  const slug = guia.path.replace(/\//g, "") || "guia";
  function descargarHtml() {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `habitia-guia-${slug}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
    showToast("Guía HTML descargada.", "success");
  }
  async function doExport(tipo) {
    setDescOpen(false);
    if (tipo === "html") return descargarHtml();
    setExportando(true);
    try {
      await descargarFlyerPdf(html, `habitia-guia-${slug}.pdf`);
      showToast("PDF descargado. Listo para adjuntar.", "success");
    } catch {
      showToast("No se pudo generar el PDF. Probá de nuevo.", "error");
    }
    setExportando(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={topbarStyle}>
        <button onClick={onClose} style={backBtn} aria-label="Volver a materiales"><FaArrowLeft size={12} /> {isMobile ? "" : "Materiales"}</button>
        <div style={{ flex: 1, minWidth: 0, fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {guia.navLabel}
        </div>
        <button onClick={copiarLink} style={primaryBtn}>
          {copiado ? <FaCheck size={12} /> : <FaLink size={12} />} {copiado ? "Copiado" : (isMobile ? "Link" : "Copiar link")}
        </button>
        <div style={{ position: "relative" }}>
          <button onClick={() => setDescOpen((o) => !o)} disabled={exportando} style={ghostBtn} aria-haspopup="menu" aria-expanded={descOpen}>
            <FaDownload size={12} /> {exportando ? "Generando…" : (isMobile ? "" : "Descargar")} <FaChevronDown size={9} />
          </button>
          {descOpen && (
            <>
              <div onClick={() => setDescOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div role="menu" style={descMenu}>
                <button onClick={() => doExport("pdf")} style={descItem}><FaRegFilePdf size={12} color={colors.error} /> PDF</button>
                <button onClick={() => doExport("html")} style={descItem}><FaCode size={12} color={colors.gray600} /> HTML</button>
              </div>
            </>
          )}
        </div>
        {!isMobile && (
          <a href={url} target="_blank" rel="noreferrer" style={{ ...ghostBtn, textDecoration: "none" }}>
            <FaExternalLinkAlt size={11} /> Ver en la web
          </a>
        )}
      </div>
      <div style={{ padding: `${spacing[2]}px ${spacing[3]}px`, background: colors.primaryLighter, color: colors.primary, fontSize: typography.sizes.sm, flexShrink: 0 }}>
        Guía pública de habitia.ai — el contenido se edita en la web, no acá. El PDF sale con tu contacto de Materiales{agente.tel ? ` (${agente.tel})` : ""}.
      </div>
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", background: "#E9EBEE", padding: isMobile ? 10 : spacing[4] }}>
        <iframe ref={iframeRef} title={`Guía: ${guia.navLabel}`} srcDoc={html} onLoad={fitIframe} scrolling="no"
          style={{ width: "100%", maxWidth: 920, margin: "0 auto", border: 0, display: "block", background: "#ffffff" }} />
      </div>
    </div>
  );
}
